import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Upload, Trash2, FileText, RefreshCw, Wand2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { ts, P, F } from '../api';
import { Page, Section, Field, Table, Td, Pill, Empty, Loading, useLoad, useTS, when } from '../ui';
import { WebsiteLearning } from './TSSetup';

const RISK_TONE = { low: 'green', medium: 'amber', high: 'red' };
const lines = (v) => (v || []).join('\n');
const toLines = (s) => s.split('\n').map(x => x.trim()).filter(Boolean);

export default function TSBrain() {
  const session = useTS();
  const agentic = session.can(P.AI) && session.has(F.AI_BRAIN);
  const trainer = session.can(P.COMPANY);
  const [rev, setRev] = useState(0);
  return (
    <Page title="AI Brain" subtitle={agentic
      ? 'Train your AI on your business, ask about your pipeline, and let the AI do routine work. Anything customer-facing waits for a person to approve.'
      : 'Train your AI on your business. It answers your main number from what it learns here.'}>
      {trainer && <WebsiteLearning manage />}
      {trainer && <BrainBuilder onTrained={() => setRev(r => r + 1)} />}
      {trainer && <BrainFiles key={rev} />}
      {agentic && <AgenticBrain />}
      {trainer && <BrainConfig />}
    </Page>
  );
}

const KIND_LABEL = { fact: 'Fact', service: 'Service', policy: 'Policy', faq: 'Question' };

// No files? The owner answers a few questions, the AI drafts what the
// receptionist should know, and only what the owner keeps trains it.
function BrainBuilder({ onTrained }) {
  const [state, setState] = useState(null);
  const [answers, setAnswers] = useState({});
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  useEffect(() => {
    ts.brainInterview().then(s => { setState(s); setAnswers(s.answers || {}); setItems(s.items || []); }).catch(err => toast.error(err.message));
  }, []);
  if (!state) return null;
  const setItem = (id, patch) => setItems(list => list.map(i => (i.id === id ? { ...i, ...patch } : i)));
  const draft = async () => {
    setBusy('draft');
    try {
      const s = await ts.draftBrain(answers);
      setState(s); setItems(s.items);
      toast.success('Draft ready. Check it, then train your AI.');
    } catch (err) { toast.error(err.message); } finally { setBusy(''); }
  };
  const train = async () => {
    setBusy('train');
    try {
      const s = await ts.trainBrain(items);
      setState(s); setItems(s.items);
      toast.success(`Training your AI on ${s.kept} item${s.kept === 1 ? '' : 's'}`);
      onTrained();
    } catch (err) { toast.error(err.message); } finally { setBusy(''); }
  };
  const kept = items.filter(i => i.keep).length;
  const showQuestions = open || state.status === 'none';
  return (
    <Section title={<span className="inline-flex items-center gap-1"><Wand2 className="w-4 h-4 text-violet-500" /> No files? Build it with AI</span>}
      actions={state.status !== 'none' && <Button size="sm" variant="outline" onClick={() => setOpen(o => !o)} data-testid="ts-builder-edit-answers">{open ? 'Hide your answers' : 'Edit your answers'}</Button>}>
      <p className="text-xs text-muted-foreground">Tell the AI about your business in your own words. It drafts what your receptionist should know, including the questions callers in your industry usually ask. You check the draft, and only what you keep trains your AI.</p>
      {state.status === 'trained' && <p className="text-xs text-emerald-600 dark:text-emerald-400" data-testid="ts-builder-trained">Your AI is trained on {items.filter(i => i.keep).length} item{items.filter(i => i.keep).length === 1 ? '' : 's'} you kept. Change anything below and train again.</p>}
      {showQuestions && (
        <div className="space-y-3" data-testid="ts-builder-questions">
          {state.questions.map(q => (
            <Field key={q.id} label={q.label} hint={q.hint}>
              <Textarea rows={q.id === 'about' || q.id === 'faqs' ? 3 : 2} value={answers[q.id] || ''}
                onChange={e => setAnswers(a => ({ ...a, [q.id]: e.target.value }))} data-testid={`ts-builder-${q.id}`} />
            </Field>
          ))}
          <div className="flex justify-end">
            <Button onClick={draft} disabled={!!busy} data-testid="ts-builder-draft"><Sparkles className="w-4 h-4 mr-1" /> {busy === 'draft' ? 'Drafting…' : items.length ? 'Draft again' : 'Draft my AI Brain'}</Button>
          </div>
        </div>
      )}
      {items.length > 0 && (
        <div className="space-y-2" data-testid="ts-builder-draft-list">
          <p className="text-xs text-muted-foreground">Ticked items train your AI. <b>Please confirm</b> marks what the AI suggested and you haven't said yet: fix it and tick it, or leave it unticked.</p>
          <ul className="divide-y divide-border">
            {items.map(i => (
              <li key={i.id} className="py-2 flex gap-3 items-start" data-testid={`ts-builder-item-${i.id}`}>
                <input type="checkbox" className="mt-2" checked={!!i.keep} onChange={e => setItem(i.id, { keep: e.target.checked })} aria-label="Keep this item" data-testid="ts-builder-keep" />
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted-foreground">{KIND_LABEL[i.kind]}</span>
                    {!i.from_owner && <Pill tone="amber">Please confirm</Pill>}
                  </div>
                  {i.kind === 'faq' ? (
                    <>
                      <Input value={i.q} onChange={e => setItem(i.id, { q: e.target.value })} aria-label="Question" />
                      <Input value={i.a} placeholder="Your answer" onChange={e => setItem(i.id, { a: e.target.value })} aria-label="Answer" data-testid="ts-builder-answer" />
                    </>
                  ) : <Input value={i.text} onChange={e => setItem(i.id, { text: e.target.value })} aria-label={KIND_LABEL[i.kind]} />}
                </div>
              </li>
            ))}
          </ul>
          <div className="flex justify-end">
            <Button onClick={train} disabled={!!busy || kept === 0} data-testid="ts-builder-train">{busy === 'train' ? 'Training…' : `Train my AI (${kept})`}</Button>
          </div>
        </div>
      )}
    </Section>
  );
}

const STATUS_TONE = { learning: 'amber', ready: 'green', failed: 'red' };
const STATUS_LABEL = { learning: 'Learning…', ready: 'Learned', failed: 'Failed' };
const kb = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function BrainFiles() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const input = useRef(null);
  const load = () => ts.brainFiles().then(setData).catch(err => toast.error(err.message));
  const working = data && (data.files.some(f => f.status === 'learning') || data.brain.status === 'building');
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!working) return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (files) => {
    setBusy(true);
    for (const file of Array.from(files || [])) {
      try { await ts.uploadBrainFile(file); toast.success(`${file.name} uploaded. The AI is learning it.`); } catch (err) { toast.error(`${file.name}: ${err.message}`); }
    }
    if (input.current) input.current.value = '';
    setBusy(false);
    load();
  };
  const remove = async (f) => {
    if (!window.confirm(`Delete ${f.name}? The AI will forget what it learned from it.`)) return;
    try { await ts.deleteBrainFile(f.id); load(); } catch (err) { toast.error(err.message); }
  };
  const rebuild = async () => {
    try { await ts.buildBrain(); toast.success('Rebuilding your AI Brain'); setTimeout(load, 1500); } catch (err) { toast.error(err.message); }
  };
  if (!data) return null;
  const b = data.brain;
  const p = b.profile || {};
  return (
    <Section title={<span className="inline-flex items-center gap-1"><FileText className="w-4 h-4 text-violet-500" /> Train your AI with your files</span>}
      actions={<Button size="sm" onClick={() => input.current?.click()} disabled={busy || data.files.length >= data.limits.max_files} data-testid="ts-brain-upload"><Upload className="w-4 h-4 mr-1" /> {busy ? 'Uploading…' : 'Upload files'}</Button>}>
      <input ref={input} type="file" multiple hidden accept={data.limits.types.join(',')} onChange={e => upload(e.target.files)} data-testid="ts-brain-file-input" />
      <p className="text-xs text-muted-foreground">Price lists, menus, FAQs, policies, product sheets, scripts. PDF, Word, Excel, CSV or text, up to 10 MB each and {data.limits.max_files} files. We keep the text, not the file. When your files and your website disagree, your files win.</p>
      {data.files.length === 0 ? <Empty>No files yet. Upload your first one to teach the AI.</Empty> : (
        <ul className="divide-y divide-border" data-testid="ts-brain-files">
          {data.files.map(f => (
            <li key={f.id} className="py-2 text-sm" data-testid={`ts-brain-file-${f.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-foreground">{f.name}</span>
                <span className="text-xs text-muted-foreground">{f.kind} · {kb(f.size)}</span>
                <Pill tone={STATUS_TONE[f.status]}>{STATUS_LABEL[f.status] || f.status}</Pill>
                <span className="flex-1" />
                {f.digest && <button className="text-xs text-primary" onClick={() => setOpen(open === f.id ? null : f.id)}>{open === f.id ? 'Hide' : 'What it learned'}</button>}
                <button className="text-muted-foreground hover:text-red-500" onClick={() => remove(f)} aria-label={`Delete ${f.name}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              {f.error && <p className="text-xs text-red-500">{f.error}</p>}
              {open === f.id && f.digest && (
                <div className="mt-2 text-xs text-muted-foreground space-y-1">
                  {f.digest.summary && <p>{f.digest.summary}</p>}
                  <ul className="list-disc pl-5">{(f.digest.facts || []).slice(0, 12).map((x, i) => <li key={i}>{x}</li>)}</ul>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="rounded-lg border border-border p-3 space-y-2" data-testid="ts-brain-built" data-status={b.status}>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium text-foreground">Your AI Brain</span>
          {b.status === 'building' && <Pill tone="amber">Building…</Pill>}
          {b.status === 'ready' && <Pill tone="green">Ready</Pill>}
          {b.status === 'failed' && <Pill tone="red">Failed</Pill>}
          <span className="flex-1" />
          {data.files.length > 0 && <Button size="sm" variant="outline" onClick={rebuild} disabled={b.status === 'building'} data-testid="ts-brain-rebuild"><RefreshCw className="w-4 h-4 mr-1" /> Rebuild</Button>}
        </div>
        {b.status === 'ready' ? (
          <>
            <p className="text-xs text-muted-foreground">Built from {b.files_used} file{b.files_used === 1 ? '' : 's'}{b.website_used ? ' and your website' : ''}. Your receptionist answers from this.</p>
            {p.summary && <p className="text-sm text-foreground">{p.summary}</p>}
            <div className="flex flex-wrap gap-1">{(p.services || []).slice(0, 12).map(x => <Pill key={x.name} tone="blue">{x.name}{x.price ? ` · ${x.price}` : ''}</Pill>)}</div>
            {(p.facts || []).length > 0 && <ul className="text-xs text-muted-foreground list-disc pl-5">{p.facts.slice(0, 8).map((x, i) => <li key={i}>{x}</li>)}</ul>}
          </>
        ) : b.status === 'failed' ? <p className="text-xs text-red-500">{b.error}</p>
          : <p className="text-xs text-muted-foreground">{data.files.length ? 'Building from your files and website…' : 'Your AI answers from your website until you upload files.'}</p>}
      </div>
    </Section>
  );
}

function AgenticBrain() {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const [busy, setBusy] = useState(false);
  const tools = useLoad(() => ts.aiTools());
  const actions = useLoad(() => ts.aiActions());

  const ask = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setAnswer(await ts.aiAsk(question)); actions.reload(); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const decide = async (id, approve) => {
    try { await (approve ? ts.aiApprove(id) : ts.aiReject(id)); actions.reload(); toast.success(approve ? 'Approved and run' : 'Rejected'); } catch (err) { toast.error(err.message); }
  };

  return (
    <>
      <Section title="Ask">
        <form onSubmit={ask} className="flex gap-2">
          <Input value={question} onChange={e => setQuestion(e.target.value)} placeholder="Which callbacks are overdue? What should I do next?" data-testid="ts-ai-question" />
          <Button type="submit" disabled={busy || !question.trim()} data-testid="ts-ai-ask"><Sparkles className="w-4 h-4 mr-1" /> Ask</Button>
        </form>
        {answer && (
          <div className="text-sm space-y-2" data-testid="ts-ai-answer">
            <p className="whitespace-pre-wrap">{answer.answer}</p>
            {(answer.actions || []).map(a => (
              <div key={a.action_id} className="text-xs"><Pill tone={a.status === 'executed' ? 'green' : 'amber'}>{a.status}</Pill> {a.tool || ''} {a.message || ''}</div>
            ))}
          </div>
        )}
      </Section>
      <Section title="Waiting for approval">
        {actions.loading ? <Loading /> : (actions.data || []).filter(a => a.status === 'proposed').length === 0 ? <Empty>Nothing waiting.</Empty> : (
          <Table head={['Action', 'Details', 'Requested by', '']}>
            {actions.data.filter(a => a.status === 'proposed').map(a => (
              <tr key={a.id}>
                <Td><Pill tone={RISK_TONE[a.risk]}>{a.risk}</Pill> {a.tool}</Td>
                <Td className="text-xs max-w-sm break-words">{JSON.stringify(a.args)}</Td>
                <Td>{a.member_name} ({a.origin === 'ai' ? 'AI suggestion' : 'person'})</Td>
                <Td className="whitespace-nowrap text-right">
                  <Button size="sm" onClick={() => decide(a.id, true)}>Approve</Button>
                  <Button size="sm" variant="ghost" onClick={() => decide(a.id, false)}>Reject</Button>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
      <Section title="What the AI can do">
        <Table head={['Action', 'Risk', 'Runs on its own?', 'Your role']}>
          {(tools.data || []).map(t => (
            <tr key={t.name}>
              <Td><div className="font-medium">{t.description}</div><div className="text-xs text-muted-foreground">{t.name}</div></Td>
              <Td><Pill tone={RISK_TONE[t.risk]}>{t.risk}</Pill></Td>
              <Td>{t.auto_approved ? 'Yes' : 'Needs approval'}</Td>
              <Td>{t.in_plan === false ? <span className="text-muted-foreground">Business plan</span> : t.allowed ? 'Allowed' : <span className="text-muted-foreground">Not allowed</span>}</Td>
            </tr>
          ))}
        </Table>
      </Section>
      <Section title="Activity log">
        {(actions.data || []).length === 0 ? <Empty>No AI activity yet.</Empty> : (
          <Table head={['When', 'Action', 'Status', 'By']}>
            {actions.data.slice(0, 50).map(a => (
              <tr key={a.id}><Td>{when(a.created_at)}</Td><Td>{a.tool}</Td><Td><Pill tone={a.status === 'executed' || a.status === 'approved' ? 'green' : a.status === 'proposed' ? 'amber' : 'red'}>{a.status}</Pill>{a.error && <span className="text-xs text-muted-foreground"> {a.error}</span>}</Td><Td>{a.member_name}</Td></tr>
            ))}
          </Table>
        )}
      </Section>
    </>
  );
}

function BrainConfig() {
  const session = useTS();
  const b = session.company.brain || {};
  const [form, setForm] = useState({
    products_services: lines(b.products_services), qualification_questions: lines(b.qualification_questions),
    approved_scripts: lines(b.approved_scripts), sales_goals: b.sales_goals || '',
    appointment_requirements: b.appointment_requirements || '', compliance_notes: b.compliance_notes || '',
    knowledge_base: (b.knowledge_base || []).map(k => `${k.q} | ${k.a}`).join('\n'),
    objection_handling: (b.objection_handling || []).map(k => `${k.objection} | ${k.response}`).join('\n'),
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const pairs = (s, a, b2) => toLines(s).map(l => { const [x, ...y] = l.split('|'); return { [a]: x.trim(), [b2]: y.join('|').trim() }; });
  const save = async () => {
    try {
      await ts.updateSection('brain', {
        products_services: toLines(form.products_services), qualification_questions: toLines(form.qualification_questions),
        approved_scripts: toLines(form.approved_scripts), sales_goals: form.sales_goals,
        appointment_requirements: form.appointment_requirements, compliance_notes: form.compliance_notes,
        knowledge_base: pairs(form.knowledge_base, 'q', 'a'), objection_handling: pairs(form.objection_handling, 'objection', 'response'),
      });
      await session.refresh();
      toast.success('AI Brain updated');
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Section title={`Teach the AI about your business (${session.company.business_type || 'other'})`} actions={<Button size="sm" onClick={save} data-testid="ts-brain-save">Save</Button>}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Products and services (one per line)"><Textarea rows={4} value={form.products_services} onChange={set('products_services')} /></Field>
        <Field label="Qualification questions (one per line)"><Textarea rows={4} value={form.qualification_questions} onChange={set('qualification_questions')} /></Field>
        <Field label="Sales goals"><Textarea rows={3} value={form.sales_goals} onChange={set('sales_goals')} /></Field>
        <Field label="Approved scripts (one per line)"><Textarea rows={3} value={form.approved_scripts} onChange={set('approved_scripts')} /></Field>
        <Field label="Questions & answers (question | answer per line)"><Textarea rows={4} value={form.knowledge_base} onChange={set('knowledge_base')} /></Field>
        <Field label="Objections (objection | response per line)"><Textarea rows={4} value={form.objection_handling} onChange={set('objection_handling')} /></Field>
        <Field label="Appointment requirements"><Textarea rows={2} value={form.appointment_requirements} onChange={set('appointment_requirements')} /></Field>
        <Field label="Compliance notes"><Textarea rows={2} value={form.compliance_notes} onChange={set('compliance_notes')} /></Field>
      </div>
    </Section>
  );
}
